import { Component, Input, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { ReactiveFormsModule, FormGroup, Validators, FormArray, FormControl } from '@angular/forms';
import {
  CIPXQuoteRequestIntakeForm,
  CIPXQuoteRequestIntakeFormCommunicationsContact,
  businessTypes,
} from '../../intake-form.types';
import { Subject, take, takeUntil } from 'rxjs';
import { GenericType } from '@sbd-doug-frontend/shared/lib/ui/quick-search/quick-search.types';
import { CIPXQuoteRequestIntakeFormService } from '../../intake-form.service';
import { CURRENT_USER_TOKEN, SharedUser, SharedUserService } from '@sbd-doug-frontend/shared/lib/types/current-user';
import { stages, updateStageConfig } from '../intake-form-bond-lookup/stage-config';
import { CIPXSearchableSelectComponent } from '@sbd-doug-frontend/shared/lib/shared-components/searchable-select/searchable-select.component';

@Component({
  selector: 'cipx-quote-request-intake-form-principal-selection',
  template: `
    <div class="cipx-form-section">
      <fieldset>
        <legend class="cipx-form-section-title">Who's this bond for?</legend>

        <div class="cipx-form-form-group">
          <mat-radio-group
            aria-label="Select Principal Type"
            class="cipx-form-mat-radio-group-rounded"
            [formControl]="quoteRequestForm.controls.principalSelection.controls.principalSelectionType">
            @if (canUseMyself) {
              <mat-radio-button
                value="myself"
                class="cipx-full-width-radio"
                [ngClass]="{
                  'cipx-full-width-radio-selected':
                    quoteRequestForm.controls.principalSelection.controls.principalSelectionType.value === 'myself',
                }">
                <div class="cipx-full-width-radio-label">
                  <span>For Myself</span>
                </div>
              </mat-radio-button>
            }
            <mat-radio-button
              value="new"
              class="cipx-full-width-radio"
              [ngClass]="{
                'cipx-full-width-radio-selected':
                  quoteRequestForm.controls.principalSelection.controls.principalSelectionType.value === 'new',
              }">
              <div class="cipx-full-width-radio-label">
                <span>New Principal</span>
              </div>
            </mat-radio-button>
            <mat-radio-button
              value="existing"
              class="cipx-full-width-radio"
              [ngClass]="{
                'cipx-full-width-radio-selected':
                  quoteRequestForm.controls.principalSelection.controls.principalSelectionType.value === 'existing',
              }">
              <div class="cipx-full-width-radio-label">
                <span>Existing Principal</span>
              </div>
            </mat-radio-button>
          </mat-radio-group>
        </div>

        @if (quoteRequestForm.controls.principalSelection.controls.principalSelectionType.value === 'existing') {
          <div class="cipx-form-form-group">
            <cipx-searchable-select
              [formGroup]="quoteRequestForm.controls.principalSelection"
              controlName="existingPrincipal"
              label="Select existing principal"
              placeholder="Search principals..."
              [options]="principals"
              valueKey="value"
              labelKey="label"
              [multiple]="false"
              [useOptionAsValue]="true"
              [compact]="false"
              [errorMessage]="
                quoteRequestForm.controls.principalSelection.controls.existingPrincipal.hasError('required')
                  ? 'Please select an existing principal'
                  : ''
              "
              (selectionChange)="onExistingPrincipalSelected($event)"></cipx-searchable-select>
          </div>
          @if (principalDetailLoading()) {
            <div class="cipx-form-form-group">
              <div class="text-sm text-gray-600 dark:text-white">Loading principal…</div>
            </div>
          } @else if (principalDetail()) {
            <div class="cipx-form-form-group">
              <div class="cipx-full-width-radio cipx-full-width-radio-selected">
                <div class="cipx-full-width-radio-label w-full">
                  <div class="flex flex-col w-full">
                    <div class="font-semibold">
                      {{ principalDetail()?.company_name }}
                      <span *ngIf="isPrincipalMeDetail()">(Me)</span>
                      <span *ngIf="principalDetail()?.company_dba">dba {{ principalDetail()?.company_dba }}</span>
                    </div>
                    <div class="mt-1 text-sm text-gray-700 dark:text-black">
                      {{ principalDetail()?.company_address_street_1 }}
                      <span *ngIf="principalDetail()?.company_address_street_2">
                        , {{ principalDetail()?.company_address_street_2 }}
                      </span>
                    </div>
                    <div class="text-sm text-gray-700 dark:text-black">
                      {{ principalDetail()?.company_address_city }},
                      {{ principalDetail()?.company_address_state_abbreviation }}
                      {{ principalDetail()?.company_address_zip }}
                    </div>
                    <div class="mt-3 text-sm font-medium">Indemnitors</div>
                    <div class="mt-1" *ngIf="principalDetail()?.owners?.length">
                      <ul class="list-none pl-0 m-0">
                        @for (owner of principalDetail()?.owners; track $index) {
                          <li class="py-0.5 dark:text-black">{{ owner.first_name }} {{ owner.last_name }}</li>
                        }
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        }
        @if (
          quoteRequestForm.controls.principalSelection.controls.principalSelectionType.value === 'myself' &&
          principalDetail() &&
          !principalDetailLoading()
        ) {
          <div class="cipx-form-form-group">
            <div class="cipx-full-width-radio cipx-full-width-radio-selected">
              <div class="cipx-full-width-radio-label w-full">
                <div class="flex flex-col w-full">
                  <div class="font-semibold">
                    {{ principalDetail()?.company_name }}
                    <span *ngIf="isPrincipalMeDetail()">(Me)</span>
                    <span *ngIf="principalDetail()?.company_dba">dba {{ principalDetail()?.company_dba }}</span>
                  </div>
                  <div class="mt-1 text-sm text-gray-700 dark:text-black">
                    {{ principalDetail()?.company_address_street_1 }}
                    <span *ngIf="principalDetail()?.company_address_street_2">
                      , {{ principalDetail()?.company_address_street_2 }}
                    </span>
                  </div>
                  <div class="text-sm text-gray-700 dark:text-black">
                    {{ principalDetail()?.company_address_city }},
                    {{ principalDetail()?.company_address_state_abbreviation }}
                    {{ principalDetail()?.company_address_zip }}
                  </div>
                  <div class="mt-3 text-sm font-medium">Indemnitors</div>
                  <div class="mt-1" *ngIf="principalDetail()?.owners?.length">
                    <ul class="list-none pl-0 m-0">
                      @for (owner of principalDetail()?.owners; track $index) {
                        <li class="py-0.5 dark:text-black">{{ owner.first_name }} {{ owner.last_name }}</li>
                      }
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
        @if (
          showMyselfNoPrincipalsMessage &&
          quoteRequestForm.controls.principalSelection.controls.principalSelectionType.value === 'myself'
        ) {
          <div class="cipx-form-form-group">
            <div class="cipx-info-message">
              Looks like you haven't requested a quote before. We'll save your information for reuse after you request a
              quote for the first time.
            </div>
          </div>
        }
      </fieldset>
    </div>
  `,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatRadioModule,
    CIPXSearchableSelectComponent,
  ],
})
export class CIPXQuoteRequestIntakeFormPrincipalSelectionComponent implements OnInit, OnDestroy {
  @Input() quoteRequestForm!: FormGroup<CIPXQuoteRequestIntakeForm>;
  principals: GenericType[] = [];
  private _intakeFormService = inject(CIPXQuoteRequestIntakeFormService);
  private _cdr = inject(ChangeDetectorRef);
  principalDetail = this._intakeFormService.selectedPrincipalDetail;
  principalDetailLoading = this._intakeFormService.principalDetailLoading;
  private _currentUser = inject<SharedUserService | null>(CURRENT_USER_TOKEN, {
    optional: true,
  }) as SharedUserService | null;
  canUseMyself = !!this._currentUser;
  showMyselfNoPrincipalsMessage = false;
  private _isResetting = false;
  private _selectionInterrupt$ = new Subject<void>();
  private _destroy$ = new Subject<void>();
  private _currentPrincipalUserId(): string | number | null {
    const cu = this._currentUser?.current?.();
    return (cu?.principal_user_id ?? null) as string | number | null;
  }

  isPrincipalMeOption(option: GenericType): boolean {
    const myId = this._currentPrincipalUserId();
    if (!myId) return false;
    const userId = this._intakeFormService.getUserIdForAccessRight(option?.value);
    return String(userId ?? '') === String(myId);
  }

  isPrincipalMeDetail(): boolean {
    const myId = this._currentPrincipalUserId();
    if (!myId) return false;
    const detail: any = this.principalDetail();
    const userId = detail?.user_id ?? null;
    return String(userId ?? '') === String(myId);
  }

  private _prefillFromAccountUser(u: SharedUser | null): void {
    this.quoteRequestForm.controls.user_id.setValue(u?.principal_user_id ?? null);
    this.quoteRequestForm.controls.bondLookup.controls.company_type_id.setValue(3);
    this._intakeFormService.companyTypeIdChanged.emit(3);
    this._intakeFormService.companyNumberOwnersChanged.emit(1);
    this.quoteRequestForm.controls.requestDetails.controls.principal.patchValue({
      company_name: u?.name ?? '',
    });
    const owners = this.quoteRequestForm.controls.requestDetails.controls.owners;
    setTimeout(() => {
      if (owners.length > 0) {
        owners.at(0).patchValue({
          owner_first_name: (u?.firstName ?? u?.name?.split(' ')[0]) || '',
          owner_last_name: (u?.lastName ?? u?.name?.split(' ').slice(1).join(' ')) || '',
          owner_email: u?.email ?? '',
          owner_phone: u?.phone ?? '',
        });
      }
    }, 0);
  }

  private _applyMyselfCreateFlow(u: SharedUser | null): void {
    this._intakeFormService.selectedPrincipalDetail.set(null);
    this._intakeFormService.clearStoredCommunicationData();
    this.showMyselfNoPrincipalsMessage = true;
    this._prefillFromAccountUser(u);
    this._cdr.markForCheck();
  }

  private _loadMyselfPrincipal(): void {
    if (!this._currentUser) {
      this._applyMyselfCreateFlow(null);
      return;
    }

    this._currentUser
      .get()
      .pipe(takeUntil(this._selectionInterrupt$), take(1))
      .subscribe((u) => {
        const principalUserId = u?.principal_user_id;
        if (!principalUserId) {
          this._applyMyselfCreateFlow(u);
          return;
        }

        this._intakeFormService
          .findPrincipalByUserId(principalUserId, { useCache: false })
          .pipe(takeUntil(this._selectionInterrupt$))
          .subscribe((rec) => {
            const accessRightId = rec?.account_user_access_right_id;
            if (accessRightId == null) {
              this._applyMyselfCreateFlow(u);
              return;
            }

            this.showMyselfNoPrincipalsMessage = false;
            this._intakeFormService
              .loadAndApplyPrincipalByAccessRightId(accessRightId, this.quoteRequestForm)
              .pipe(takeUntil(this._selectionInterrupt$))
              .subscribe(() => this._cdr.markForCheck());
          });
      });
  }

  getCompanyTypeLabel(): string {
    const idStr = this.principalDetail()?.company_type_id ?? null;
    const id = idStr !== null ? Number(idStr) : null;
    if (id === null || isNaN(id)) {
      return '';
    }
    const match = businessTypes().find((t) => t.value === id);
    return match ? match.label : '';
  }

  private _loadPrincipalsForSelection(selection: 'new' | 'existing' | 'myself' | null): void {
    // Interrupt any in-flight async operations when loading for a new selection, then start a fresh scope
    // IMPORTANT: Do NOT interrupt when selection === 'myself' because we may have just started the user-info request
    if (selection !== 'myself') {
      this._selectionInterrupt$.next();
      this._selectionInterrupt$.complete();
      this._selectionInterrupt$ = new Subject<void>();
    }
    // Skip loading if we're in the middle of a reset
    if (this._isResetting) {
      return;
    }

    if (selection === 'myself') {
      this._loadMyselfPrincipal();
    } else if (selection === 'existing') {
      this._intakeFormService
        .getPrincipals()
        .pipe(takeUntil(this._selectionInterrupt$))
        .subscribe((items) => {
          this.principals = items.map((principal) => ({
            ...principal,
            label: `${principal.label}${this.isPrincipalMeOption(principal) ? ' (Me)' : ''}`,
          }));
          this.showMyselfNoPrincipalsMessage = false;
          this._cdr.markForCheck();
        });
    } else {
      // 'new' selection doesn't need principals
      this.principals = [];
      this.showMyselfNoPrincipalsMessage = false;
      this._cdr.markForCheck();

      // Ensure bond lookup stages are reset to defaults on initial/new selection
      stages.forEach((stageConfig) => {
        updateStageConfig(stageConfig, stageConfig.defaultActive, true);
      });
    }
  }

  ngOnInit(): void {
    const principalSelectionGroup = this.quoteRequestForm.controls.principalSelection;
    const selectionTypeControl = principalSelectionGroup.controls.principalSelectionType;
    const existingPrincipalControl = principalSelectionGroup.controls.existingPrincipal;

    // Conditional required: existingPrincipal is required when selection type is 'existing'
    selectionTypeControl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      // Interrupt any in-flight async work tied to previous selection, then start a fresh scope
      this._selectionInterrupt$.next();
      this._selectionInterrupt$.complete();
      this._selectionInterrupt$ = new Subject<void>();
      // Skip processing if we're in the middle of a reset
      if (this._isResetting) {
        return;
      }

      if (value === 'existing') {
        existingPrincipalControl.setValidators([Validators.required]);
      } else {
        existingPrincipalControl.clearValidators();
        existingPrincipalControl.setValue('');

        if (value === 'new') {
          this._intakeFormService.selectedPrincipalDetail.set(null);
          this._intakeFormService.clearStoredCommunicationData();
          this._isResetting = true;
          const targetValue = value;

          setTimeout(() => {
            this._selectionInterrupt$.next();
            this._selectionInterrupt$.complete();
            this._selectionInterrupt$ = new Subject<void>();
            this._intakeFormService.resetForm.emit();
            setTimeout(() => {
              this.quoteRequestForm.controls.principalSelection.controls.principalSelectionType.setValue(
                targetValue as any,
                { emitEvent: false }
              );
              this.quoteRequestForm.controls.bondLookup.controls.company_type_id.setValue(3);
              this._intakeFormService.companyTypeIdChanged.emit(3);
              this._isResetting = false;
              this._loadPrincipalsForSelection(targetValue as any);
            }, 100);
          }, 0);
        }
      }
      existingPrincipalControl.updateValueAndValidity();
    });

    // If default selection is 'myself', set user_id immediately from current user
    if (selectionTypeControl.value === 'myself' && this._currentUser) {
      const current = this._currentUser.current?.() ?? null;
      if (current && current.principal_user_id) {
        this.quoteRequestForm.controls.user_id.setValue(current.principal_user_id);
      } else {
        this._currentUser
          .get()
          .pipe(take(1))
          .subscribe((u) => {
            this.quoteRequestForm.controls.user_id.setValue(u?.principal_user_id ?? null);
          });
      }
    }

    // Initial load based on default selection
    this._loadPrincipalsForSelection(selectionTypeControl.value as any);

    // Reload when selection changes
    selectionTypeControl.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe((val) => this._loadPrincipalsForSelection(val as any));
  }

  onExistingPrincipalSelected(selected: GenericType) {
    if (!selected || !selected.value) {
      return;
    }
    this._intakeFormService
      .getPrincipalDetail(selected.value)
      .pipe(takeUntil(this._selectionInterrupt$))
      .subscribe((detail) => {
        // Persist user_id alongside selection using principals list mapping
        const userId = this._intakeFormService.getUserIdForAccessRight(selected.value);
        this.quoteRequestForm.controls.user_id.setValue(userId);
        const requestDetailsGroup = this.quoteRequestForm.controls.requestDetails;
        // Prefill principal fields
        requestDetailsGroup.controls.principal.patchValue({
          company_name: detail.company_name ?? '',
          company_dba: detail.company_dba ?? '',
          company_address_street_1: (detail as any).company_address_street_1 ?? '',
          company_address_street_2: (detail as any).company_address_street_2 ?? '',
          company_address_city: (detail as any).company_address_city ?? '',
          company_address_state_abbreviation: (detail as any).company_address_state_abbreviation ?? '',
          company_address_zip: (detail as any).company_address_zip ?? '',
          company_address_county: (detail as any).company_address_county ?? '',
        });

        // Toggle addCompanyAddress if company address fields are present
        const hasCompanyAddress = Boolean(
          (detail as any).company_address_id ||
            (detail as any).company_address_street_1 ||
            (detail as any).company_address_city ||
            (detail as any).company_address_state_abbreviation ||
            (detail as any).company_address_zip
        );
        if (hasCompanyAddress) {
          requestDetailsGroup.controls.addCompanyAddress.setValue(true);
        }

        // Persist user_id for request submission (from principals list we only had access_right_id, detail response should contain user context; if not, fallback from selected option won't have it)
        this.quoteRequestForm.controls.user_id.setValue(
          (this.principalDetail()?.company_type_id, (this.principalDetail() as any)?.user_id ?? null)
        );

        // Company type id (lives in bondLookup)
        const companyTypeId = Number(detail.company_type_id ?? 3);
        this.quoteRequestForm.controls.bondLookup.controls.company_type_id.setValue(companyTypeId);
        this._intakeFormService.companyTypeIdChanged.emit(companyTypeId);

        // Owners mapping
        const owners = Array.isArray(detail.owners) ? detail.owners : [];
        const ownersCount = owners.length > 0 ? owners.length : 1;
        requestDetailsGroup.controls.company_years_in_business.setValue(
          Number((detail as any).company_years_in_business ?? 0)
        );
        // Let the IntakeForm add/remove controls
        this._intakeFormService.companyNumberOwnersChanged.emit(ownersCount);

        // Store communication data for Notifications step to consume later
        this._intakeFormService.storeCommunicationData(
          (detail as any).communication?.communication_preferences ?? null,
          (detail as any).communication?.principal_contacts ?? null
        );

        // Patch owners after array is resized by parent
        setTimeout(() => {
          const ownersArray = requestDetailsGroup.controls.owners;
          if (ownersArray.length !== ownersCount) {
            setTimeout(() => this.applyOwners(owners), 0);
            return;
          }
          this.applyOwners(owners);
        }, 0);
      });
  }

  private applyOwners(owners: any[]) {
    const ownersArray = this.quoteRequestForm.controls.requestDetails.controls.owners;
    for (let i = 0; i < ownersArray.length && i < owners.length; i++) {
      const src = owners[i];
      const dst = ownersArray.at(i);
      dst.patchValue({
        owner_first_name: src.first_name ?? '',
        owner_middle_name: src.middle_name ?? '',
        owner_last_name: src.last_name ?? '',
        owner_email: src.email ?? '',
        owner_phone: src.phone ?? '',
        owner_ssn: src.ssn ?? '',
        owner_marital_status: src.marital_status_id ?? null,
        owner_dob: src.dob ?? '',
        owner_years_experience: src.years_experience ? Number(src.years_experience) : null,
        owner_percent_owned: src.percent_owned ? Number(src.percent_owned) : null,
        owner_title: '',
        owner_address_street_1: src.home_address_street_1 ?? '',
        owner_address_street_2: src.home_address_street_2 ?? '',
        owner_address_city: src.home_address_city ?? '',
        owner_address_state_abbreviation: src.home_address_state_abbreviation ?? '',
        owner_address_zip: src.home_address_zip ?? '',
      });
    }
  }

  private clearAllCommunicationsConfiguration(): void {
    const communications = this.quoteRequestForm.controls.communications as unknown as FormGroup;
    const contactsArray =
      (communications.get('contacts') as FormArray<
        FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>
      > | null) ?? null;
    if (contactsArray) {
      // Reset to a single account_user contact with cleared values and all toggles disabled
      while (contactsArray.length > 0) {
        contactsArray.removeAt(0);
      }
    }

    console.log('🧹 Cleared all communications configuration for new principal');
  }

  private buildCommunicationsForNewPrincipal(): void {
    const communications = this.quoteRequestForm.controls.communications as unknown as FormGroup;
    const contactsArray = communications.get('contacts') as FormArray<
      FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>
    > | null;
    if (!contactsArray) return;

    // Preserve or create account_user at index 0
    let preserved = contactsArray.at(0);
    if (!preserved || preserved.get('contact_type')?.value !== 'account_user') {
      preserved = new FormGroup({
        contact_type: new FormControl<'account_user' | 'user' | 'listener'>('account_user', { nonNullable: true }),
        user_id: new FormControl<number | null>(null),
        first_name: new FormControl(''),
        last_name: new FormControl(''),
        email: new FormControl(''),
        phone: new FormControl(''),
        sms_phone: new FormControl(''),
        email_enabled: new FormControl<boolean>(false, { nonNullable: true }),
        phone_enabled: new FormControl<boolean>(false, { nonNullable: true }),
        sms_enabled: new FormControl<boolean>(false, { nonNullable: true }),
        renewal_email_enabled: new FormControl<boolean>(false, { nonNullable: true }),
        renewal_phone_enabled: new FormControl<boolean>(false, { nonNullable: true }),
        email_editable: new FormControl<boolean>(false, { nonNullable: true }),
      } as any);
    }
    while (contactsArray.length > 0) {
      contactsArray.removeAt(contactsArray.length - 1);
    }
    if (preserved) {
      contactsArray.push(preserved);
    }

    // Prefill account_user name/email/phone from current user if available
    const au = preserved as FormGroup;
    const emailCtrl = au.get('email') as FormControl;
    const phoneCtrl = au.get('phone') as FormControl;
    const smsCtrl = au.get('sms_phone') as FormControl;
    const cu = this._currentUser?.current?.();
    if (cu) {
      (au.get('first_name') as FormControl).setValue(cu.firstName ?? '');
      (au.get('last_name') as FormControl).setValue(cu.lastName ?? '');
      emailCtrl.setValue(cu.email ?? '');
      phoneCtrl.setValue(cu.phone ?? '');
      smsCtrl.setValue(cu.phone ?? '');
    }

    // At principal selection step we don't know owners yet; defer owner/listener contacts to notifications step
  }

  ngOnDestroy(): void {
    // Interrupt any in-flight selection-related work and clean up subscriptions
    this._selectionInterrupt$.next();
    this._selectionInterrupt$.complete();
    this._destroy$.next();
    this._destroy$.complete();
  }
}
